import { Validators } from '@angular/forms';
import {
  FormField,
  FormFieldList,
  SfAmountInputFormField,
  SfCheckboxFormField,
  SfDatePickerFormField,
  SfFileUploadFormField,
  SfInputFormField,
  SfSelectData,
  SfSelectFormField,
  SfSelectOption,
  SfTextAreaFormField,
  SfToggleFormField,
} from 'ngx-smart-form';
import { IFormField } from 'ngx-smart-form/lib/smart-form/interfaces/i-form-field';
import { MaxLengthCounterField } from '../../history/max-lenght-counter-field';
import { Finanziamenti } from '../../requests/models/cms-finanziamenti';
import { SfAddressField } from './sf-address-field';

export interface ICmsField {
  element: {
    description: string;
    value: string;
    id?: string;
  }[];
  informations: string | undefined;
  label: string;
  name: string;
  placeholder: string;
  required: boolean;
  type: CmsFieldType;
  parent?: string;
  parentMatchValue?: any;
  title?: string;
  subtitle?: string;
  description?: string;
  default?: { value?: string; description?: string };
  slice?: number;
  decimals?: number;
  currency?: string;
  disabled?: boolean;
  max?: number | string;
  min?: number | string;
}

export interface MappedCmsSection {
  title: string;
  subtitle: string;
  description: string;
  type: string;
}

export type MappedCmsSectionWithFields = MappedCmsSection & {
  fields: FormField[];
};

export type MappedCmsSectionWithFieldList = MappedCmsSection & {
  fields: FormFieldList;
};

type ExtendedField = IFormField &
  SfSelectData<string | (SfSelectOption & { id?: string })>;

const parseBoolean = (value: string | undefined) => {
  if (value === 'true' || value === 'false') {
    return value === 'true';
  } else {
    return value;
  }
};

export class CmsField {
  static fieldTypeMap = new Map<
    CmsFieldType,
    (data: ExtendedField) => FormField
  >([
    [
      'AREA',
      (fieldData: ExtendedField) => new MaxLengthCounterField(fieldData, 4000),
    ],
    [
      'CHECKBOX',
      (fieldData: ExtendedField) =>
        new SfCheckboxFormField({
          ...fieldData,
          value: fieldData.value || false,
        }),
    ],
    [
      'DATEPICKER',
      (fieldData: ExtendedField) =>
        new SfDatePickerFormField({
          ...fieldData,
          value: fieldData.value ? new Date(fieldData.value) : undefined,
        }),
    ],
    [
      'NUMBER',
      (fieldData: ExtendedField) => new SfAmountInputFormField(fieldData),
    ],
    ['TEXT', (fieldData: ExtendedField) => new SfInputFormField(fieldData)],
    ['SELECT', (fieldData: ExtendedField) => new SfSelectFormField(fieldData)],
    ['TOGGLE', (fieldData: ExtendedField) => new SfToggleFormField(fieldData)],
    [
      'UPLOAD',
      (fieldData: ExtendedField) => new SfFileUploadFormField(fieldData),
    ],
    ['ADDRESS', (fieldData: ExtendedField) => new SfAddressField(fieldData)],
  ]);
  static genField<T>(
    field: ICmsField,
    item?: T,
    hasExtendedValue: boolean = false
  ) {
    const createField = CmsField.fieldTypeMap.get(field.type);

    let options: SfSelectOption<string | (SfSelectOption & { id?: string })>[] =
      [];
    if (field.type === 'SELECT' && field.element) {
      options = field.element.map((o) => ({
        view: o.description,
        value: !hasExtendedValue
          ? o.value
          : { value: o.value, view: o.description, id: o.id },
      }));
    }
    let max = field.max !== undefined ? Number(field.max) : undefined;
    max = !max || isNaN(max) ? undefined : max;

    const newField: (FormField & { slice?: number }) | undefined = createField
      ? createField({
          ...field,
          formControlName: field.name,
          value: item ? item[field.name as keyof T] : field.default?.value,
          disabled:
            field.disabled !== undefined
              ? field.disabled
              : !!field.default?.value,
          info: field.informations,
          options,
          compareWithFunction: hasExtendedValue
            ? (optionValue, controlValue) =>
                (
                  optionValue as SfSelectOption<string> & {
                    id?: string | undefined;
                  }
                ).id ===
                (
                  controlValue as SfSelectOption<string> & {
                    id?: string | undefined;
                  }
                )?.id
            : (optionValue, controlValue) => optionValue === controlValue,
          parentMatchValue: parseBoolean(field.parentMatchValue),
          validators: max !== undefined ? [Validators.max(max)] : undefined,
        })
      : undefined;

    if (newField) {
      newField.slice = field.slice;
    }
    return newField;
  }

  static genFieldList<T>(cmsFields: ICmsField[], item?: T) {
    let fields = cmsFields
      .map((el) => {
        return CmsField.genField<T>(el, item);
      })
      .filter((el) => !!el) as (FormField<T> & { slice?: number })[];
    let sliced: FormFieldList = [];
    for (let i = 0; i < fields.length; i++) {
      let slice = fields.slice(i, i + (fields[i].slice || 2));
      if (
        Finanziamenti.isStandaloneField(slice[0]) ||
        Finanziamenti.isStandaloneField(slice[1])
      ) {
        slice.pop();
        sliced.push(slice);
      } else {
        sliced.push(slice);
        i = i + slice.length - 1;
      }
    }
    return sliced;
  }
}

export type CmsFieldType =
  | 'NUMBER'
  | 'TEXT'
  | 'CHECKBOX'
  | 'TOGGLE'
  | 'RADIO'
  | 'SELECT'
  | 'DATEPICKER'
  | 'UPLOAD'
  | 'AREA'
  | 'ADDRESS';
